import { publicRegistry } from '@/i18n/dictionaries/publicRegistry';
import { useTranslation } from '@/i18n/useTranslation';
import {
  DOMAIN_COVERAGE_NOTE,
  domainCoverageNote,
} from '@/features/publicsite/registry/domainCoverage';
import type { AdvisorDomainCoverageProps } from '@/features/publicsite/types/registry/AdvisorDomainCoverageProps';

/**
 * ToR §6.3's coverage map read by subject rather than by territory: for each domain of competence,
 * how many advisers work in it and which raions have advisers but none of them in that domain.
 *
 * The block above it answers "where is there nobody at all"; this one answers the question a
 * farmer actually arrives with, which is whether anyone near them advises on their subject. A
 * raion with four advisers and none in zootechnics is covered on the territorial map and empty to
 * the farmer who keeps cattle.
 *
 * Collapsed per domain, as the territorial gaps are: the pairs run to seven domains against
 * thirty-six raions, and opened at once they would bury the search this page exists for.
 *
 * Which of three things a row says is domainCoverageNote's to decide, not this file's: there is no
 * DOM in the unit suite, so a rule left in the JSX is a rule nothing can check — which is how this
 * block came to tell every visitor that a domain with no advisers had every raion covered.
 */
export function AdvisorDomainCoverage({ rows }: AdvisorDomainCoverageProps) {
  const { t } = useTranslation(publicRegistry);
  if (rows.length === 0) return null;

  return (
    <div className="advisors-domains">
      <h2 className="advisors-domains__heading">{t('advisorsDomainsHeading')}</h2>
      {rows.map((row) => {
        const note = domainCoverageNote(row);
        const summary = (
          <>
            <span className="advisors-domains__name">{row.label}</span>
            {' — '}
            {note === DOMAIN_COVERAGE_NOTE.noAdvisors
              ? t('advisorsDomainNoAdvisors')
              : t('advisorsDomainAdvisors', { count: row.advisors })}
            {note === DOMAIN_COVERAGE_NOTE.gaps && (
              <>
                {' · '}
                {t('advisorsDomainGaps', { count: row.gapZones.length })}
              </>
            )}
          </>
        );
        // Only the row with raions to name has anything to open.
        return note === DOMAIN_COVERAGE_NOTE.gaps ? (
          <details key={row.domainId} className="advisors-domains__row">
            <summary className="muted">{summary}</summary>
            <div className="chip-row advisors-coverage__chips">
              {row.gapZones.map((zone) => (
                <span key={zone.territorialUnitId} className="chip">
                  {zone.name}
                </span>
              ))}
            </div>
          </details>
        ) : (
          <p key={row.domainId} className="muted advisors-domains__row">
            {summary}
          </p>
        );
      })}
    </div>
  );
}
